import React, {
	type PropsWithChildren,
	useCallback,
	useEffect,
	useId,
	useRef,
	useState,
} from 'react';

import { FlagsProvider } from '@atlaskit/flag';
import { useNotifyOpenLayerObserver } from '@atlaskit/layering/experimental/open-layer-observer';
import { useIsFhsEnabled } from '@atlaskit/navigation-system/fhs-rollout';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, media, xcss } from '@atlaskit/primitives';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { KeyboardHighlightProvider } from '@atlassian/search-dialog';
import { STATSIG_QUICK_FIND_LAYER } from '@atlassian/search-experiment';

import { SearchExperience } from '../../common/constants/search';
import type { QueryParams } from '../../common/types';
import {
	AppContextProvider,
	type AppContextProviderProps,
	useAppContext,
} from '../../common/ui/app-context';
import MessagesIntlProvider from '../../common/ui/message-intl-provider/messages-intl-provider';
import type { TextfieldProps } from '../../common/ui/quick-find/textfield';
import { getInitialQuery } from '../../common/ui/quick-find/textfield/input-skeleton';
import { WidthObserverProvider } from '../../common/ui/width-observer';
import { defaultGenerateSearchUrl } from '../../common/utils/url';
import { QuickFindResults } from '../../controllers/quick-find/fetch-results';
import { QuickFindPostQueryVitalStatsProvider } from '../../controllers/quick-find/quickfind-post-query-vitalstats-context';
import BootstrapProvider from '../../controllers/store/bootstrap/bootstrap-provider';
import { type BootstrapContainerProps } from '../../controllers/store/bootstrap/bootstrap-store-provider';
import { useQuickFindActions, useQuickFindOpen } from '../../controllers/store/quick-find';
import { QuickFindStoreProvider } from '../../controllers/store/quick-find/quick-find-store-provider';
import { SearchStoreProvider } from '../../controllers/store/search/search-store-provider';

import { SearchDialog } from './dialog';
import { FiltersRow } from './filters-row';
import { SearchTextField } from './textfield';
import { useCloseManager } from './utils/use-close-manager';
import { usePreQueryDwellTimeAnalytics } from './utils/use-pre-query-dwell-time-analytics';
import { useSyncStates } from './utils/use-sync-states';

const textFieldBoxStyles = xcss({
	width: '100%',
	position: 'relative',
});

const minWidthStyles = xcss({
	left: '50%',
	transform: 'translate(-50%, 0)', // Anchor Quick Find input and dialog to the center of the viewport
	position: 'fixed',
	maxWidth: '780px', // To retain the same width as Quick Search
	zIndex: 'navigation',
	[media.above.lg]: {
		maxWidth: 'unset',
		position: 'relative',
	},
});

export type QuickFindComponentProps = {
	open?: boolean;
	setOpen?: (open: boolean) => void;
	postQueryDisabled?: boolean;
	textfieldComponent?: React.ComponentType<TextfieldProps>;
	isSSR?: boolean;
	PlacementPO?: () => JSX.Element;
	showFilters?: boolean;
	showFiltersRow?: boolean;
	searchRefreshStyling?: boolean;
	customPlaceholder?: string;
	onGhostTextGenerated?: (ghostText: string) => void;
	inputRef?: React.RefObject<HTMLInputElement>;
};

type QuickFindProps = BootstrapContainerProps & AppContextProviderProps & QuickFindComponentProps;

const QuickFindComponent = ({
	open,
	setOpen,
	postQueryDisabled,
	textfieldComponent,
	isSSR,
	PlacementPO,
	showFilters = true,
	showFiltersRow = false,
	searchRefreshStyling = false,
	customPlaceholder,
	onGhostTextGenerated,
	inputRef,
}: QuickFindComponentProps) => {
	const inputBoxRef = useRef<HTMLDivElement>(null);
	const dialogRef = useRef<HTMLDivElement>(null);
	const { isNav4Enabled, queryParams } = useAppContext();
	const internalInputRef = useRef<HTMLInputElement>(null);
	const effectiveInputRef = inputRef || internalInputRef;

	const [localQuery, setLocalQuery] = useState(() =>
		getInitialQuery((typeof queryParams.text === 'string' && queryParams.text) || ''),
	);

	const { setQuickFindOpen } = useQuickFindActions();

	const quickFindOpen = useQuickFindOpen();

	const dialogId = useId();

	const setMinWidthStyles =
		isNav4Enabled &&
		!textfieldComponent && // min-width styles is only supported with the default text field
		quickFindOpen;

	useSyncStates(quickFindOpen, setQuickFindOpen, open, setOpen);

	useCloseManager({ dialogRef, inputBoxRef });
	usePreQueryDwellTimeAnalytics();

	const isFhsEnabled = useIsFhsEnabled();

	const handleOpenLayerObserverCloseSignal = useCallback(() => {
		if (!isFhsEnabled || !fg('platform-dst-side-nav-layering-fixes')) {
			return;
		}

		setQuickFindOpen(false);
	}, [isFhsEnabled, setQuickFindOpen]);

	/**
	 * We need to notify the open layer observer when the quick find dialog is open.
	 * This is required for several navigation-system features to work correctly, such as:
	 * - Closing layers when the user resizes a layout slot (e.g. SideNav, Panel)
	 * - Ensuring correct layer stacking order when a popup is open within the TopNav or SideNav
	 *
	 * Ideally, the dialog would be using the ADS Popup component instead, which handles this internally
	 * among other benefits.
	 */
	useNotifyOpenLayerObserver({
		isOpen: isFhsEnabled && fg('platform-dst-side-nav-layering-fixes') && quickFindOpen,
		type: 'popup',
		onClose: handleOpenLayerObserverCloseSignal,
	});

	return (
		<>
			<QuickFindResults />
			<Box xcss={[textFieldBoxStyles, setMinWidthStyles && minWidthStyles]} ref={inputBoxRef}>
				<KeyboardHighlightProvider listenerNode={effectiveInputRef?.current}>
					<SearchTextField
						dialogId={dialogId}
						inputRef={effectiveInputRef}
						localQuery={localQuery}
						setLocalQuery={setLocalQuery}
						textfieldComponent={textfieldComponent}
						postQueryDisabled={postQueryDisabled}
						customPlaceholder={customPlaceholder}
						onGhostTextGenerated={onGhostTextGenerated}
					/>
					{!isSSR && (
						<FlagsProvider>
							{quickFindOpen && (
								<WidthObserverProvider>
									<SearchDialog
										dialogId={dialogId}
										dialogRef={dialogRef}
										inputRef={effectiveInputRef}
										localQuery={localQuery}
										setLocalQuery={setLocalQuery}
										PlacementPO={PlacementPO}
										showFilters={showFilters}
										searchRefreshStyling={searchRefreshStyling}
									/>
								</WidthObserverProvider>
							)}
						</FlagsProvider>
					)}
					{showFiltersRow && <FiltersRow />}
				</KeyboardHighlightProvider>
			</Box>
		</>
	);
};

export const QuickFindShell = ({
	children,
	queryParams: actualQueryParams,
	callbacks,
	...props
}: PropsWithChildren<QuickFindProps>): React.JSX.Element => {
	const { onSearchCompleted } = callbacks ?? {};
	/**
	 * Quick Find does not need to set queryParams, hence using an object
	 * in a state variable as the queryParams object. Any interactions with
	 * the url should be done through onNavigate in the AppContext.
	 */
	const [queryParams, setQueryParams] = useState<QueryParams>({ text: actualQueryParams?.text });

	/**
	 * Quick Find only reacts to changes in the text query parameter when the
	 * user is on the search page.
	 */
	const previousTextQueryParam = useRef<string | undefined>(actualQueryParams?.text);
	useEffect(() => {
		if (actualQueryParams?.text === previousTextQueryParam.current) {
			return;
		}

		let onSearchRoute = true; // Default to true in case URL parsing fails

		try {
			const generateSearchUrl = props.generateSearchUrl || defaultGenerateSearchUrl;
			const searchUrl = new URL(generateSearchUrl({}));
			const searchPath = searchUrl.pathname;

			const currentUrl = new URL(window.location.href);
			const currentPath = currentUrl.pathname;

			onSearchRoute = searchPath === currentPath;
		} catch {
			// ignore error
		}

		if (onSearchRoute) {
			setQueryParams((previousQueryParams) => ({
				...previousQueryParams,
				text: actualQueryParams?.text,
			}));
		}

		previousTextQueryParam.current = actualQueryParams?.text;
	}, [actualQueryParams?.text, props.generateSearchUrl]);

	return (
		<BootstrapProvider
			{...props}
			queryParams={queryParams}
			callbacks={{ onSearchCompleted, updateQueryParameters: setQueryParams }}
			statsigLayer={STATSIG_QUICK_FIND_LAYER}
			experience={SearchExperience.QUICK_FIND}
		>
			<SearchStoreProvider experience={SearchExperience.QUICK_FIND}>
				<QuickFindStoreProvider>
					<QuickFindPostQueryVitalStatsProvider>
						<AppContextProvider {...props} queryParams={queryParams}>
							{children}
						</AppContextProvider>
					</QuickFindPostQueryVitalStatsProvider>
				</QuickFindStoreProvider>
			</SearchStoreProvider>
		</BootstrapProvider>
	);
};

export const QuickFind = ({
	// eslint-disable-next-line no-unused-vars
	children,
	...props
}: PropsWithChildren<QuickFindProps>): React.JSX.Element => {
	return (
		<UFOSegment name="quick-find">
			<MessagesIntlProvider experience={SearchExperience.QUICK_FIND}>
				<RelayEnvironmentProvider>
					<QuickFindShell {...props}>
						<QuickFindComponent {...props} />
					</QuickFindShell>
				</RelayEnvironmentProvider>
			</MessagesIntlProvider>
		</UFOSegment>
	);
};
